// @givmo/org-console — the tiers, and the ONE place this console's display names live.
//
// ⚠ THE WIRE VALUE AND THE DISPLAY NAME DIVERGE, on purpose. The backend's org-wide base role is
// `Owner` | `Member`, and that is what the roster row carries and what every request body must send.
// The console renders `Owner` as **"Global Admin"**, because in a multi-module suite "Owner" reads as
// "owns this module" while the fact being stated is "administers everything this organization has" —
// and per-module Owner/Member tiers exist on the very same screen, so two different things would
// otherwise both be called Owner in one view.
//
// So: NEVER put a display name on the wire, and never compare against one. `ROLE_OWNER` /
// `ROLE_MEMBER` are the wire values and are what every gate and every request body uses;
// {@link tierLabel} is display-only and its output must not reach a request. Keeping both in this one
// module is what makes that separation checkable — a `'Global Admin'` literal anywhere else in this
// package is a bug.

/** The org-wide base tiers, as the WIRE spells them. */
export type OrgTier = 'Owner' | 'Member';

export const ROLE_OWNER: OrgTier = 'Owner';
export const ROLE_MEMBER: OrgTier = 'Member';

/** The console's display name for the `Owner` tier. See the header for why it is not "Owner". */
export const GLOBAL_ADMIN_LABEL = 'Global Admin';
/** The console's display name for the `Member` tier — unchanged from the wire value. */
export const MEMBER_LABEL = 'Member';

/**
 * The console's display name for an org-wide tier.
 *
 * Tolerant: an unresolved or unrecognized role renders as "Member", the least-privilege reading,
 * rather than inventing a tier or printing a raw wire value into the DOM.
 */
export function tierLabel(role: string | null | undefined): string {
  return (role ?? '').trim() === ROLE_OWNER ? GLOBAL_ADMIN_LABEL : MEMBER_LABEL;
}

/**
 * What a Global Admin can do, in one sentence, in this host's own noun — the Global Admin card's line
 * on the invite and in the access editor (D-CPO-77: it says what the role means for people, the
 * Admin Console included).
 *
 * Takes the noun as DATA rather than naming an entity class — the seam this whole package is built
 * on. See `org-console-config.ts`.
 */
export function globalAdminDescription(orgNoun: string): string {
  return `Manages everyone and every app in this ${orgNoun}, including the Admin Console.`;
}

/**
 * The badge on an app the organization has switched off, wherever a list of apps shows one: the
 * access editor's apps here, and a host's own app pickers (D-EP-76). One rule, one string, in the
 * host's own noun as DATA.
 */
export function offForOrgLabel(orgNoun: string): string {
  return `Off for the ${orgNoun}`;
}

/**
 * The ORG-LEVEL word, only where it says something (OM-96, D-CPO-77 item 7): "Global Admin" for a
 * Global Admin, and nothing (`null`) for anyone else.
 *
 * ⚠ "MEMBER" AT TWO LEVELS READ AS A CONTRADICTION. The roster's Access column, an invite's "As" and
 * the corner's standing said "Member" for everyone who isn't a Global Admin, beside an app column
 * saying Member or Owner — "Access: Member, Donations: Owner" read as two answers to one question
 * (the user test). So the org-level word is said only for a Global Admin; anyone else's level is
 * per app, and the app's own column or page says it. Display-only, like {@link tierLabel}.
 */
export function orgLevelWord(role: string | null | undefined): string | null {
  return (role ?? '').trim() === ROLE_OWNER ? GLOBAL_ADMIN_LABEL : null;
}

/** What a plain Member can do, in one sentence. */
export function memberDescription(orgNoun: string): string {
  return `Can use the apps they hold, but cannot manage this ${orgNoun}'s team or its apps.`;
}

/**
 * What an OWNER of one app can do, in one sentence — the per-module tier's description (shell
 * contract v3, R6/T7.3, copy per the mockup pass: plain words, no jargon).
 *
 * ⚠ THE WORD "OWNER" IS THE PER-MODULE TIER'S, and this is one of the two places it may be spelled
 * in copy (the other is {@link moduleTierLabel}). The org-level role is never "Owner" in copy.
 */
export function moduleOwnerDescription(appLabel: string): string {
  return `${ROLE_OWNER} of ${appLabel} — can change the app's settings and approve access.`;
}

/** What a MEMBER of one app can do, in one sentence — the per-module tier's description. */
export function moduleMemberDescription(appLabel: string): string {
  return `${ROLE_MEMBER} of ${appLabel} — day-to-day work.`;
}

// ── Per-MODULE tiers ─────────────────────────────────────────────────────────
//
// ⚠ THE SAME WIRE STRING MEANS TWO DIFFERENT THINGS ON ONE SCREEN, which is exactly why the org-wide
// rename above exists. `Owner` as a BASE role means "administers everything this organization has"
// and renders as the Global Admin label; `Owner` as a module's `granted_role` means "holds this
// module's org-binding acts" and renders as the plain word. The access editor shows both at once — a
// person's org tier at the top, their per-module tier per row — so one shared labeller would have
// made the screen say the same thing about two different facts.

/**
 * Display name for a module's `granted_role`.
 *
 * Tolerant in the same direction as {@link tierLabel}: an unrecognized value renders as Member, the
 * least-privilege reading, matching the backend's own read-side rule that an unknown `granted_role`
 * resolves to the Member catalog.
 */
export function moduleTierLabel(grantedRole: string | null | undefined): string {
  return (grantedRole ?? '').trim() === ROLE_OWNER ? ROLE_OWNER : ROLE_MEMBER;
}

/**
 * Coerce a module's `granted_role` to a canonical tier for a CONTROL's value.
 *
 * Distinct from {@link moduleTierLabel} by intent even though the two agree today: this one's output
 * goes back on the WIRE, so it must stay a wire value forever, while the labeller may diverge from it
 * the moment the display name for a module tier changes.
 */
export function moduleTier(grantedRole: string | null | undefined): OrgTier {
  return (grantedRole ?? '').trim() === ROLE_OWNER ? ROLE_OWNER : ROLE_MEMBER;
}
